<script setup lang="ts">
import {onMounted, onBeforeUnmount, ref, watch} from 'vue';
import {SvgIcon} from '../../../svg.ts';
import {initAllSpice} from '../../../features/allspice.ts';

const props = withDefaults(defineProps<{
  codeConversationsContainer?: HTMLElement;
  pageId: string;
  warnForOtherPages?: boolean;
}>(), {
  codeConversationsContainer: null,
  warnForOtherPages: true,
});

const showFileComments = ref(false);
const fileConversationElements = ref<{codeConversationIndex: number; el: HTMLElement}[]>([]);
const pageConversationElements = ref<{codeConversationIndex: number; el: HTMLElement}[]>([]);

onMounted(() => {
  initAllSpice();
});

onBeforeUnmount(() => {
  const container = props.codeConversationsContainer;
  const renderedConversationsContainer = document.querySelector('.code-conversations');
  if (!container || !renderedConversationsContainer) return;

  const existingConversations = renderedConversationsContainer.querySelectorAll('[data-conversation-index]');
  for (const existingConversationContainer of existingConversations) {
    const indexStr = existingConversationContainer.getAttribute('data-conversation-index');
    const index = indexStr ? parseInt(indexStr) : -1;
    const existingConversation = existingConversationContainer.children[0];
    if (index < 0 || !existingConversation) continue;

    const originalConversation = container.children[index];
    if (originalConversation !== existingConversation) {
      originalConversation.replaceWith(existingConversation);
    }
  }

  const newConversations = Array.from(renderedConversationsContainer.children).filter(
    (el) =>
      !el.hasAttribute('data-conversation-index') &&
      el.classList.contains('add-comment'),
  );

  for (const newConversation of newConversations) {
    const conversationHolder = newConversation.querySelector('.conversation-holder');
    const firstChild = conversationHolder?.firstElementChild;
    if (firstChild?.id) {
      // Once a comment is created, it will have an id.
      container.append(newConversation);
    }
  }
});

const processComments = () => {
  if (!props.codeConversationsContainer) return;

  const children = Array.from(props.codeConversationsContainer.children) as HTMLElement[];
  fileConversationElements.value = [];
  pageConversationElements.value = [];

  for (const [index, child] of children.entries()) {
    const item = {codeConversationIndex: index, el: child};

    const conversations = Array.from(child.querySelectorAll('[data-allspice-subpath]'));

    const subpath = conversations[0]?.getAttribute('data-allspice-subpath');
    const isFileLevelConversation = subpath === null;
    const isCurrentPageLevelConversation = subpath === props.pageId;

    const conversationHolder = child.querySelector('.conversation-holder');
    const firstChild = conversationHolder?.firstElementChild;
    const isDraftComment = !firstChild?.id;

    if (isCurrentPageLevelConversation) {
      pageConversationElements.value.push(item);
    } else if ((props.warnForOtherPages || isFileLevelConversation) && !isDraftComment) {
      fileConversationElements.value.push(item);
    }
  }
};

watch(
  () => props.codeConversationsContainer,
  () => processComments(),
  {immediate: true},
);

const shouldShowPageComment = (el: Element) => {
  return el.querySelector(`[data-allspice-subpath="${props.pageId}"]`) !== null;
};
</script>

<template>
  <div class="code-conversations">
    <div
      v-if="props.warnForOtherPages && fileConversationElements.length > 0"
      class="file-comments-header tw:flex tw:items-center tw:justify-between tw:bg-primary-light-7 tw:border-primary"
    >
      <div class="tw:flex tw:items-center tw:gap-1">
        <SvgIcon
          :name="'octicon-info'"
          class="tw:mr-1 tw:text-primary"
        />
        There {{ fileConversationElements.length === 1 ? 'is' : 'are' }}
        <b>{{ fileConversationElements.length }}
          additional conversation{{ fileConversationElements.length === 1 ? '' : 's' }}</b> related to other parts of this design
      </div>
      <button
        class="ui tiny labeled button tw:hover:underline tw:text-primary"
        @click="showFileComments = !showFileComments"
      >
        {{ showFileComments ? 'Hide' : 'Show' }} additional conversation{{ fileConversationElements.length === 1 ? '' : 's' }}
      </button>
    </div>
    <div
      v-show="showFileComments"
      class="file-comments tw:bg-primary-light-7 tw:border-primary"
    >
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-for="(item, index) in fileConversationElements"
        v-show="showFileComments"
        :key="index"
        :data-conversation-index="item.codeConversationIndex"
        v-html="item.el.outerHTML"
      />
      <!-- eslint-enable vue/no-v-html -->
    </div>
    <!-- eslint-disable vue/no-v-html -->
    <div
      v-for="(item, index) in pageConversationElements"
      v-show="shouldShowPageComment(item.el)"
      :key="index"
      :data-conversation-index="item.codeConversationIndex"
      v-html="item.el.outerHTML"
    />
    <!-- eslint-enable vue/no-v-html -->
  </div>
</template>

<style scoped>
.file-comments {
  border-bottom-left-radius: var(--border-radius);
  border-bottom-right-radius: var(--border-radius);
  border-style: dashed;
  border-top: none;
  border-width: 1px;
  margin: 0 4px;
}

.file-comments-header {
  border-radius: var(--border-radius);
  border-width: 1px;
  padding: 8px;
}
</style>
